import { Button } from '@tempo-io/sui-button';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@tempo-io/sui-dropdownmenu';
import { Add } from '@tempo-io/sui-icons';
import { DropdownMenu as RadixDropdownMenu } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import type { IVisualizationHeaderProps } from '@/lib/api/types/loop-visualization';
import { useTimelineHeaderVisibilityMutation } from '@/lib/mutations/loop-visualization/useTimelineLayoutMutations';
import { useTimelineHeaderConfig } from '@/lib/queries/loop-visualization/useTimelineHeaderConfig';
import { useVisualizationByType } from '@/lib/queries/loop-visualization/useVisualization';
import { usePortalContainer } from '../PortalContainerContext';
import { RADIX_DROPDOWN_MENU_CONTENT_BASE } from '../radixBypassStyles';
import { useViewContext } from '../ViewContext';

export const TimelineAddHeaderMenu = ({
  header1FieldId,
  header2FieldId,
  allMainHeaders,
  allSubHeaders,
  subHeaderConfigs,
}: {
  header1FieldId: string;
  header2FieldId: string;
  allMainHeaders: IVisualizationHeaderProps[];
  allSubHeaders: IVisualizationHeaderProps[];
  subHeaderConfigs?: Record<string, { orderedHeaderIds: string[]; hiddenHeaderIds: string[] }>;
}) => {
  const { t } = useTranslation();
  const portalContainer = usePortalContainer();
  const { viewId } = useViewContext();

  const { data: visualization } = useVisualizationByType(viewId, 'timeline');
  const visualizationId = visualization?.visualizationId ?? 0;

  const { showHeaderMutation } = useTimelineHeaderVisibilityMutation(viewId, visualizationId);

  const { data: mainHeaderConfig } = useTimelineHeaderConfig({
    viewId,
    visualizationId,
    headerFieldId: header1FieldId || null,
  });

  const hiddenMainHeaders =
    mainHeaderConfig?.hiddenHeaderIds && mainHeaderConfig.hiddenHeaderIds.length > 0
      ? allMainHeaders.filter((h) => mainHeaderConfig.hiddenHeaderIds.includes(h.id))
      : [];

  const groupsWithHiddenSubHeaders: Array<{
    mainHeader: IVisualizationHeaderProps;
    hiddenSubs: IVisualizationHeaderProps[];
  }> = [];

  if (header2FieldId && subHeaderConfigs) {
    for (const mainHeader of allMainHeaders) {
      const config = subHeaderConfigs[mainHeader.id];
      if (!config || config.hiddenHeaderIds.length === 0) continue;
      const hiddenSet = new Set(config.hiddenHeaderIds);
      const hiddenSubs = allSubHeaders.filter((h) => hiddenSet.has(h.id));
      if (hiddenSubs.length > 0) {
        groupsWithHiddenSubHeaders.push({ mainHeader, hiddenSubs });
      }
    }
  }

  const hasHiddenHeaders = hiddenMainHeaders.length > 0 || groupsWithHiddenSubHeaders.length > 0;

  if (!hasHiddenHeaders) {
    return null;
  }

  const onShowMainHeader = (headerId: string) => {
    showHeaderMutation.mutate({
      headerId,
      headerFieldId: header1FieldId,
    });
  };

  const onShowSubHeader = (headerId: string, parentHeaderId: string) => {
    showHeaderMutation.mutate({
      headerId,
      headerFieldId: header2FieldId,
      parentFieldHeaderId: header1FieldId,
      parentHeaderId,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="mt-1 ml-1"
          aria-label={t(($) => $.loopVisualization.add.header.menu.hidden.values)}
        >
          <Add />
        </Button>
      </DropdownMenuTrigger>
      {/* RadixDropdownMenu.Content is used directly (instead of the SUI DropdownMenuContent wrapper)
          so we can supply a custom portal container in fullscreen mode. The SUI wrapper does not
          expose a `container` prop on its Portal. When the view is fullscreen, the Fullscreen API
          only renders the fullscreen element and its descendants, so portalling to document.body
          would make this menu invisible. Track SUI portal support: TODO add ticket link.
          The class string below mirrors the SUI DropdownMenuContent styles verbatim. */}
      <DropdownMenuPortal container={portalContainer ?? undefined}>
        <RadixDropdownMenu.Content
          data-slot="dropdown-menu-content"
          sideOffset={4}
          side="right"
          align="start"
          className={RADIX_DROPDOWN_MENU_CONTENT_BASE}
        >
          <DropdownMenuLabel>
            {t(($) => $.loopVisualization.add.header.menu.hidden.values)}
          </DropdownMenuLabel>
          {hiddenMainHeaders.map((header) => (
            <DropdownMenuItem key={header.id} onSelect={() => onShowMainHeader(header.id)}>
              {header.label}
            </DropdownMenuItem>
          ))}
          {groupsWithHiddenSubHeaders.map(({ mainHeader, hiddenSubs }, index) => (
            <span key={mainHeader.id}>
              {(hiddenMainHeaders.length > 0 || index > 0) && <DropdownMenuSeparator />}
              <DropdownMenuLabel>{mainHeader.label}</DropdownMenuLabel>
              {hiddenSubs.map((subHeader) => (
                <DropdownMenuItem
                  key={`${mainHeader.id}-${subHeader.id}`}
                  onSelect={() => onShowSubHeader(subHeader.id, mainHeader.id)}
                >
                  {subHeader.label}
                </DropdownMenuItem>
              ))}
            </span>
          ))}
        </RadixDropdownMenu.Content>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};
